import { Link } from "react-router-dom";
import ThemeToggle from "../../theme/theme-toggle";
import {
    faChartBar,
    faChartLine,
    faUsers,
    faCrown,
    faShoppingBag,
    faPercent,
    faLayerGroup,
    faMoneyBillWave,
    faGift,
    faCoins,
    faEnvelope,
    faBell,
    faTicketAlt,
    faImage,
    faUserPlus,
    faUserCheck,
    faVideo,
    faNewspaper,
    faComments,
    faBolt,
    faFileCode,
    faBug,
    faStar,
    faCloudArrowDown,
    faLink,
    faUserShield,
    faHashtag,
} from "@fortawesome/free-solid-svg-icons";
import SideBarOptions from "./SideBarOptions";
import { useAppSelector } from "../../store";
import { canAccessPath } from "../../config/admin-permissions";

// Nav structure shared by the desktop sidebar and the mobile drawer.
export const NAV_OPTIONS = [
    {
        title: "",
        options: [
            { path: "/dashboard", label: "Tổng quan", icon: faChartBar },
            { path: "/app-growth", label: "Tăng trưởng App", icon: faChartLine },
        ],
    },
    {
        title: "Người dùng",
        options: [
            { path: "/user", label: "Users", icon: faUsers },
            { path: "/topuser", label: "Top Users", icon: faCrown },
            { path: "/mods", label: "Quản lý CTV", icon: faUserShield },
        ],
    },
    {
        title: "Đơn hàng",
        options: [
            { path: "/order", label: "Đơn Hàng", icon: faShoppingBag },
            { path: "/commission", label: "Set Hoa Hồng", icon: faPercent },
            { path: "/reward-program", label: "Chương trình thưởng", icon: faGift },
            { path: "/xu-program", label: "Xu & Hạng", icon: faCoins },
            { path: "/app-rate-promo", label: "Chiến dịch X2 (cũ)", icon: faStar },
        ],
    },
    {
        title: "Tài chính",
        options: [
            { path: "/payment/batch", label: "Thanh toán lô", icon: faLayerGroup },
            { path: "/withdraw", label: "Rút tiền", icon: faMoneyBillWave },
            { path: "/bonus", label: "Thưởng User", icon: faGift },
        ],
    },
    {
        title: "Marketing",
        options: [
            { path: "/email", label: "Email Campaign", icon: faEnvelope },
            { path: "/push", label: "Push Notification", icon: faBell },
            { path: "/voucher", label: "Voucher", icon: faTicketAlt },
            { path: "/banner", label: "Banner", icon: faImage },
            { path: "/referral", label: "Giới thiệu", icon: faUserPlus },
            { path: "/referred-users", label: "Được giới thiệu", icon: faUserCheck },
        ],
    },
    {
        title: "Nội dung",
        options: [
            { path: "/post", label: "Bài viết Blog", icon: faNewspaper },
            { path: "/comment", label: "Bình luận", icon: faComments },
            { path: "/community", label: "Kênh cộng đồng", icon: faHashtag },
            { path: "/videolist", label: "Quản lý Video", icon: faVideo },
            { path: "/utility-block", label: "Tiện ích App", icon: faBolt },
            { path: "/page", label: "Trang HTML", icon: faFileCode },
            { path: "/guide-video", label: "Video hướng dẫn", icon: faVideo },
            { path: "/tiktok-affiliate-config", label: "Mua sắm TikTok", icon: faLink },
            { path: "/product-notes", label: "Dòng nhắc sản phẩm", icon: faLink },
        ],
    },
    {
        title: "Hệ thống",
        options: [
            { path: "/app-version-gate", label: "Nhắc cập nhật App", icon: faCloudArrowDown },
            { path: "/logs", label: "Logs & Lỗi", icon: faBug },
        ],
    },
];

/** Trang quản lý link rút gọn (site PHP riêng, có cổng mật khẩu riêng) — chỉ hiện cho admin. */
const SHORTLINK_MANAGERS = [
    { label: "Link ngắn longhousee.me", href: "https://longhousee.me/process/link-manager.php" },
    { label: "Link ngắn dealgiare.com", href: "https://dealgiare.com/process/link-manager.php" },
];

const handleLogout = () => {
    localStorage.removeItem("access_token");
};

type SideBarContentProps = {
    /** Called after a nav item / action is tapped — used to close the mobile drawer. */
    onNavigate?: () => void;
};

/**
 * Inner content of the sidebar (brand header + nav + footer links).
 * Rendered both inside the fixed desktop sidebar and the mobile Drawer.
 */
function SideBarContent({ onNavigate }: SideBarContentProps) {
    const userInfo = useAppSelector((state) => state.auth.userInfo);
    // mod (CTV): chỉ hiện các mục được cấp quyền; ẩn hết section trống. admin: full.
    const navOptions =
        userInfo?.role === "mod"
            ? NAV_OPTIONS.map((section) => ({
                  ...section,
                  options: section.options.filter((opt) => canAccessPath(userInfo, opt.path)),
              })).filter((section) => section.options.length > 0)
            : NAV_OPTIONS;

    return (
        <div className="h-full flex flex-col justify-between overflow-y-auto bg-surface">
            <div>
                <div className="p-4 border-b">
                    <p className="text-lg font-bold text-content m-0">Admin Panel</p>
                    <p className="text-xs text-content-3 m-0">longhousee.com</p>
                </div>
                <SideBarOptions options={navOptions} onNavigate={onNavigate} />
            </div>

            <div className="p-3 border-t flex flex-col gap-2">
                {/* Đặt ở đây nên tự có mặt cả sidebar desktop lẫn Drawer mobile */}
                <ThemeToggle />
                {userInfo?.role === "admin" &&
                    SHORTLINK_MANAGERS.map((item) => (
                        <a
                            key={item.href}
                            href={item.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={onNavigate}
                            className="p-2 rounded-md border text-content hover:bg-surface-muted transition-colors flex items-center justify-center text-sm gap-2"
                        >
                            {item.label}
                        </a>
                    ))}
                <a
                    href="https://php.longhousee.com/admin/"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={onNavigate}
                    className="p-2 rounded-md text-white bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 transition-all font-semibold shadow flex items-center justify-center text-sm gap-2"
                >
                    PHP Panel
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M14 3h7m0 0v7m0-7L10 17m-4 0h4a2 2 0 002-2v-4" />
                    </svg>
                </a>
                <Link
                    to="/login"
                    onClick={() => {
                        handleLogout();
                        onNavigate?.();
                    }}
                    className="p-2 flex justify-center bg-pink-600 text-white rounded-md text-sm font-medium hover:bg-pink-700 transition-colors"
                >
                    Logout
                </Link>
            </div>
        </div>
    );
}

export default SideBarContent;
